import { verifyUnitSettingsReadback } from './unitOperatingSettings.js';

function id(value) {
  const number = Number(value);
  if (!/^[1-9][0-9]*$/.test(String(value)) || !Number.isSafeInteger(number) || number > 2147483647) {
    throw new Error('Select a valid company and unit.');
  }
  return number;
}
function unitsFrom(value, companyId) {
  const units = Array.isArray(value) ? value : value?.units;
  if (!Array.isArray(units) || units.some(unit => !unit ||
      String(unit.company_id) !== String(companyId) || !Number.isSafeInteger(Number(unit.id)) || Number(unit.id) < 1)) {
    throw new Error('The unit list could not be verified. Refresh before making a change.');
  }
  return units;
}
function writeError(cause, submitted) {
  const status = cause?.response?.status;
  const uncertain = submitted || !status || status === 408 || status >= 500;
  return Object.assign(new Error(uncertain
    ? 'The save could not be confirmed. Refresh the unit list before another change.'
    : 'The unit change was rejected. Check the fields and your access.'),
  { uncertain, code: uncertain ? 'UNIT_SAVE_UNCONFIRMED' : 'UNIT_SAVE_REJECTED' });
}

// Resolve legacy mount aliases through safe GETs. Each mutation is sent once.
export function createUnitSettingsRequests(client) {
  async function list(companyId, { signal } = {}) {
    const company = id(companyId);
    const canonical = `/admin/companies/${company}/units`;
    const paths = [canonical, `/admin/companies/companies/${company}/units`, `/solo-mdsh-api${canonical}`];
    for (let index = 0; index < paths.length; index++) {
      try {
        const response = await client.get(paths[index], { signal });
        return { path: paths[index], units: unitsFrom(response.data, company) };
      } catch (error) {
        if (error?.response?.status === 404 && index < paths.length - 1) continue;
        throw error;
      }
    }
  }
  async function save(companyId, unitId, payload) {
    const company = id(companyId), target = unitId == null ? null : id(unitId);
    const resource = await list(company);
    if (target && !resource.units.some(unit => Number(unit.id) === target)) {
      throw new Error('This unit is unavailable in the selected company. Refresh the unit list.');
    }
    let submitted = false;
    try {
      const response = target
        ? await client.patch(`${resource.path}/${target}`, payload)
        : await client.post(resource.path, payload);
      submitted = true;
      const savedId = target || id(response.data?.unit?.id ?? response.data?.id);
      const readback = await client.get(resource.path);
      const units = unitsFrom(readback.data, company);
      const unit = units.find(row => Number(row.id) === savedId);
      verifyUnitSettingsReadback(unit, company, savedId, payload);
      for (const key of ['unit_name', 'slug', 'address', 'contact_email', 'is_active']) {
        if (Object.hasOwn(payload, key) && payload[key] !== undefined && unit[key] !== payload[key]) {
          throw new Error('The stored unit details do not match the submitted values.');
        }
      }
      return { unit, units };
    } catch (cause) { throw writeError(cause, submitted); }
  }
  return { list, create: (company, payload) => save(company, null, payload),
    update: (company, unit, payload) => save(company, unit, payload) };
}
